import { useEffect, useRef, type JSX } from "react";
import { useIntersectionObserver } from "usehooks-ts";

import "./GdExerciseWrapper.scss";

const DEFAULT_PATHNAME = "/loader.html";

export interface GdExerciseWrapperOpenFile {
  path: string;
  focus?: boolean;
  override?: string;
}

export interface GdExerciseWrapperProps {
  loader?: string;
  dataPath: string;
  mainScene: string;
  openFiles?: GdExerciseWrapperOpenFile[];
  height: string | number;
  hideCode?: boolean;
  hideConsole?: boolean;
  hidePlayer?: boolean;
  hideTest?: boolean;
  snippet?: string;
}

export default function GdExerciseWrapper(
  props: GdExerciseWrapperProps
): JSX.Element {
  const {
    loader = new URL(DEFAULT_PATHNAME, window.location.origin).href,
    dataPath,
    mainScene,
    openFiles = [],
    height,
    hideCode = false,
    hideConsole = false,
    hidePlayer = false,
    hideTest = false,
    snippet = "",
  } = props;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(wrapperRef, {});
  const isVisible = entry?.isIntersecting === true;

  const loaderUrl = new URL(loader);
  loaderUrl.searchParams.append("dataPath", dataPath);
  loaderUrl.searchParams.append("mainScene", mainScene);

  for (const openFile of openFiles) {
    const getFocus = (): "focus" | "nofocus" => {
      if (openFile.focus === true) {
        return "focus";
      }
      return "nofocus";
    };

    loaderUrl.searchParams.append(
      "open",
      [openFile.path, getFocus(), openFile.override]
        .filter((element) => element != null)
        .join(":")
    );
  }

  if (hideCode) {
    loaderUrl.searchParams.append("hide", "code");
  }
  if (hideConsole) {
    loaderUrl.searchParams.append("hide", "console");
  }
  if (hidePlayer) {
    loaderUrl.searchParams.append("hide", "player");
  }
  if (hideTest) {
    loaderUrl.searchParams.append("hide", "test");
  }

  if (snippet.length > 0) {
    loaderUrl.searchParams.append("snippet", snippet);
  }

  useEffect(() => {
    if (iframeRef.current == null) return;
    iframeRef.current.contentWindow?.postMessage({
      from: "gd-exercise",
      type: "low-processor-usage-mode",
      value: !isVisible,
    });
  }, [isVisible, iframeRef.current]);

  return (
    <>
      <div className="gdexercise-wrapper" ref={wrapperRef}>
        <iframe
          allow="fullscreen"
          src={loaderUrl.href}
          style={{
            height,
          }}
          ref={iframeRef}
        ></iframe>
      </div>
    </>
  );
}
